import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { ChatsTeardrop, PaperPlaneRight, MagnifyingGlass, Paperclip, FilePdf, Image as ImageIcon } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  listChatThreads, getChatThreadMessages, postChatThreadMessage,
  uploadChatThreadAttachment, getFileUrl,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ChatAttachment from '../../components/ChatAttachment';

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const MessagesPage = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    const r = await listChatThreads(search ? { q: search } : {});
    setThreads(r.data || []);
  }, [search]);

  const loadMessages = useCallback(async () => {
    if (!activeId) return;
    const r = await getChatThreadMessages(activeId);
    setThread(r.data.thread);
    setMessages(r.data.messages || []);
  }, [activeId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll for new messages every 5s when a thread is open
  useEffect(() => {
    if (!activeId) return;
    pollRef.current = setInterval(loadMessages, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !activeId) return;
    setSending(true);
    try {
      await postChatThreadMessage(activeId, text);
      setInput('');
      await Promise.all([loadMessages(), loadThreads()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be re-uploaded
    if (!file || !activeId || uploading) return;
    setUploading(true);
    try {
      await uploadChatThreadAttachment(activeId, file, input.trim() || undefined);
      setInput('');
      await Promise.all([loadMessages(), loadThreads()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex border border-border" data-testid="messages-page">
      {/* Thread list */}
      <div className="w-72 border-r border-border flex flex-col">
        <div className="p-3 border-b border-border">
          <h2 className="font-semibold text-sm flex items-center gap-2 mb-2">
            <ChatsTeardrop className="w-5 h-5" weight="duotone" /> Conversations
          </h2>
          <div className="relative">
            <MagnifyingGlass className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dealer…" className="pl-8 h-8 text-sm" data-testid="thread-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 && (
            <div className="text-xs text-muted-foreground text-center p-6">No conversations yet.</div>
          )}
          {threads.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveId(t.id)}
              className={`w-full text-left p-3 border-b border-border hover:bg-secondary/50 ${activeId === t.id ? 'bg-secondary/60' : ''}`}
              data-testid={`thread-row-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium truncate">{t.client_name}</span>
                {t.unread_by_staff > 0 && (
                  <span className="text-[10px] bg-red-600 text-white rounded-full px-1.5 py-0.5">{t.unread_by_staff}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{t.last_message_preview || '—'}</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">{t.last_message_at ? fmt(t.last_message_at) : ''}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Chat panel */}
      <div className="flex-1 flex flex-col">
        {!thread ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            Select a conversation to start
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-border">
              <p className="font-semibold">{thread.client_name}</p>
              <p className="text-xs text-muted-foreground">{thread.client_type}</p>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/10" data-testid="messages-list">
              {messages.map((m) => {
                const mine = m.sender_id === user?.id;
                const fromClient = m.sender_role === 'client';
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.id}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                      mine ? 'bg-primary text-primary-foreground rounded-br-sm' :
                      fromClient ? 'bg-blue-50 border border-blue-200 rounded-bl-sm' : 'bg-white border border-border rounded-bl-sm'
                    }`}>
                      {!mine && <p className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</p>}
                      {m.body}
                      {m.attachment && <ChatAttachment attachment={m.attachment} mine={mine} />}
                      <p className={`text-[9px] mt-1 ${mine ? 'opacity-70' : 'text-muted-foreground'}`}>{fmt(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-2 border-t border-border bg-white flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleAttachment}
                className="hidden"
                data-testid="attach-input"
              />
              <Button size="icon" variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Attach image or PDF" data-testid="attach-btn">
                <Paperclip className="w-4 h-4" />
              </Button>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={uploading ? 'Uploading…' : 'Type a message…'}
                rows={1}
                className="flex-1 resize-none min-h-[40px] max-h-[120px] text-sm"
                data-testid="message-input"
                disabled={sending || uploading}
              />
              <Button size="sm" onClick={send} disabled={sending || uploading || !input.trim()} data-testid="send-message-btn">
                <PaperPlaneRight weight="fill" className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
